import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

// HELOC: draw period (interest-only on outstanding balance) +
// repayment period (full P&I amortization of remaining balance).
export default function HelocPage() {
    const [homeValue, setHomeValue] = useState(600000);
    const [mortgageBalance, setMortgageBalance] = useState(300000);
    const [cltv, setCltv] = useState(85);
    const [drawAmount, setDrawAmount] = useState(75000);
    const [rate, setRate] = useState(9.25);
    const [drawYears, setDrawYears] = useState(10);
    const [repayYears, setRepayYears] = useState(20);

    const result = useMemo(() => {
        const hv = toNum(homeValue);
        const mb = toNum(mortgageBalance);
        const maxLine = Math.max(0, (hv * toNum(cltv)) / 100 - mb);
        const drawn = Math.min(toNum(drawAmount), maxLine);
        const r = toNum(rate);
        const drawMonthly = (drawn * (r / 100)) / 12; // interest-only
        const repayMonthly = monthlyPayment(drawn, r, Math.max(1, toNum(repayYears)));
        const totalInterestDraw = drawMonthly * 12 * Math.max(0, toNum(drawYears));
        const totalRepay = repayMonthly * 12 * Math.max(0, toNum(repayYears));
        const totalInterestRepay = totalRepay - drawn;
        return {
            maxLine,
            drawn,
            drawMonthly,
            repayMonthly,
            totalInterestDraw,
            totalInterestRepay,
            totalCost: totalInterestDraw + totalRepay,
            overLimit: toNum(drawAmount) > maxLine,
        };
    }, [homeValue, mortgageBalance, cltv, drawAmount, rate, drawYears, repayYears]);

    return (
        <CalculatorShell
            slug="heloc"
            explainer={
                                <>
                    <h2>How this HELOC calculator works</h2>
                    <p>
                        A HELOC calculator estimates your available credit line and monthly payments during
                        both the draw period and the repayment period. Use it to evaluate whether a home
                        equity line of credit makes sense for a home improvement project, debt consolidation,
                        or another large expense.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Available equity:</strong> home value multiplied by the maximum loan-to-value ratio (typically 80–85%) minus your current mortgage balance.</li>
                        <li><strong>Draw period:</strong> typically 10 years — you borrow as needed and pay interest only on the outstanding balance.</li>
                        <li><strong>Repayment period:</strong> typically 20 years — the outstanding balance is fully amortized with principal and interest payments.</li>
                        <li><strong>Variable rate:</strong> tied to the Prime Rate plus a lender margin — your payment changes as the rate changes.</li>
                        <li><strong>Draw period payment:</strong> approximately outstanding balance × monthly rate — lower than the repayment period payment.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>A HELOC is secured by your home — failure to pay can result in foreclosure, so treat it with the same seriousness as your mortgage.</li>
                        <li>If your lender offers a fixed-rate lock on a portion of the balance, consider using it to reduce rate risk on large draws.</li>
                        <li>Avoid using a HELOC to finance depreciating purchases like vehicles or vacations — reserve it for investments that hold or grow in value.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Home value" prefix="$" value={homeValue} onChange={setHomeValue} dataTestId="calc-input-homevalue" />
                        <Field label="Current mortgage balance" prefix="$" value={mortgageBalance} onChange={setMortgageBalance} dataTestId="calc-input-balance" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Max CLTV" suffix="%" value={cltv} onChange={setCltv} step={1} dataTestId="calc-input-cltv" hint="Lender cap: 80–85%" />
                        <Field label="Amount to draw" prefix="$" value={drawAmount} onChange={setDrawAmount} dataTestId="calc-input-draw" hint={`Max: ${fmtUSD(result.maxLine, { decimals: 0 })}`} />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Rate" suffix="%" value={rate} onChange={setRate} step={0.125} dataTestId="calc-input-rate" />
                        <Field label="Draw period (yr)" value={drawYears} onChange={setDrawYears} dataTestId="calc-input-drawyears" />
                        <Field label="Repay period (yr)" value={repayYears} onChange={setRepayYears} dataTestId="calc-input-repayyears" />
                    </div>
                    {result.overLimit && (
                        <div className="text-sm bg-accent/10 border border-accent/30 rounded-md p-3 text-primary" data-testid="heloc-limit-warning">
                            <strong>Draw exceeds your limit.</strong> At {cltv}% CLTV you can only
                            draw up to {fmtUSD(result.maxLine, { decimals: 0 })}. We&apos;ve capped
                            the calculation at that amount.
                        </div>
                    )}
                </div>
                <div className="lg:col-span-2 space-y-4">
                    <ResultStat
                        label="Draw period payment"
                        value={result.drawMonthly}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "Interest-only", value: result.drawMonthly, format: (n) => fmtUSD(n), testId: "calc-result-draw-interest" },
                            { label: "Draw period cost", value: result.totalInterestDraw, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-draw-total" },
                        ]}
                    />
                    <ResultStat
                        label="Repayment period payment"
                        value={result.repayMonthly}
                        primary={false}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "P&I", value: result.repayMonthly, format: (n) => fmtUSD(n), testId: "calc-result-repay-pi" },
                            { label: "Total interest", value: result.totalInterestRepay, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-repay-interest" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
